/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 **/

import { atom } from "jotai";
import { BaseService } from "@/frameworks/libraries/restservice";

class AdminGroupServiceStatic extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (groupId: string, path = "") => `/api/admin/groups${groupId}${path}`;
	private optionAPIPath = (groupId: string) => `/api/admin/groups/options/${groupId}`;
	private static Cache = {
		Options: atom<WebServices.UserAdmin.Group.Options.Response>({}),
		OptionUpdate: atom<WebServices.UserAdmin.Group.OptionUpdate.Response>({}),
		SimpleList: atom<WebServices.UserAdmin.Group.SimpleList.Response>({}),
		GroupList: atom<WebServices.UserAdmin.Group.List.Response>({}),
		GroupDetail: atom<WebServices.UserAdmin.Group.Detail.Response>({}),
		Create: atom<WebServices.UserAdmin.Group.Create.Response>({}),
		Delete: atom<WebServices.UserAdmin.Group.Delete.Response>({}),
		Update: atom<WebServices.UserAdmin.Group.Update.Response>({})
	};

	public options = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ groupId }: WebServices.UserAdmin.Group.Options.Request) => this.get(this.optionAPIPath(groupId)),
		cache: AdminGroupServiceStatic.Cache.Options
	});

	public optionUpdate = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ groupId, ...rest }: WebServices.UserAdmin.Group.OptionUpdate.Request) =>
			this.put(this.optionAPIPath(groupId), rest),
		cache: AdminGroupServiceStatic.Cache.OptionUpdate
	});

	public simpleList = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.UserAdmin.Group.SimpleList.Request) =>
			this.get(this.APIPath("", "/simple"), parameter),
		cache: AdminGroupServiceStatic.Cache.SimpleList
	});

	public groupList = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.UserAdmin.Group.List.Request) => this.get(this.APIPath("", ""), parameter),
		cache: AdminGroupServiceStatic.Cache.GroupList
	});

	public groupDetail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ groupId }: WebServices.UserAdmin.Group.Detail.Request) => this.get(this.APIPath(`/${groupId}`)),
		cache: AdminGroupServiceStatic.Cache.GroupDetail
	});

	public createGroup = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.UserAdmin.Group.Create.Request) =>
			this.post(this.APIPath("", ""), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			}),
		cache: AdminGroupServiceStatic.Cache.Create
	});

	public updateGroup = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({
			groupId,
			parameter
		}: {
			groupId: string;
			parameter: WebServices.UserAdmin.Group.Update.Request;
		}) =>
			this.post(this.APIPath(`/${groupId}`), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			}),
		cache: AdminGroupServiceStatic.Cache.Update
	});

	public deleteGroup = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ groupId }: WebServices.UserAdmin.Group.Delete.Request) => this.delete(this.APIPath(`/${groupId}`)),
		cache: AdminGroupServiceStatic.Cache.Delete
	});
}

class AdminGroupUserServiceStatic extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (groupId: string, path = "", userId?: string, path2 = "") =>
		`/api/admin/groups/${groupId}${path}${userId ? `/${userId}` : ""}${path2 ? `/${path2}` : ""}`;

	private static Cache = {
		UserDetail: atom<WebServices.UserAdmin.GroupUser.UserDetail.Response>({}),
		UpdateGroupUser: atom<WebServices.UserAdmin.GroupUser.Update.Response>({}),
		DeleteGroupUser: atom<WebServices.UserAdmin.GroupUser.Delete.Response>({}),
		GroupUserList: atom<WebServices.UserAdmin.GroupUser.List.Response>({}),
		GroupUserEmail: atom<WebServices.UserAdmin.GroupUser.EmailCheck.Response>({}),
		CreateGroupUser: atom<WebServices.UserAdmin.GroupUser.Add.Response>({}),
		Unlock: atom<WebServices.UserAdmin.GroupUser.Unlock.Response>({}),
		ResetPassword: atom<WebServices.UserAdmin.GroupUser.ResetPassword.Response>({}),
		SendEmail: atom<WebServices.UserAdmin.GroupUser.SendEmail.Response>({}),
		UserJoinedSpaceList: atom<WebServices.UserAdmin.Group.GetSpacesByGroupIdAndUserId.Response>({}),
		DownloadExcelSample: atom<WebServices.UserAdmin.GroupUser.DownloadExcelSample.Response>({}),
		ImportUsers: atom<WebServices.UserAdmin.GroupUser.ImportUsers.Response>({})
	};

	public groupUserList = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.UserAdmin.GroupUser.List.Request) =>
			this.get(this.APIPath(parameter.groupId, "/users"), parameter),
		cache: AdminGroupUserServiceStatic.Cache.GroupUserList
	});

	public deleteGroupUser = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ userId, groupId }: WebServices.UserAdmin.GroupUser.Delete.Request) =>
			this.delete(this.APIPath(groupId, "/users", userId)),
		cache: AdminGroupUserServiceStatic.Cache.DeleteGroupUser
	});

	public userJoinedSpaceList = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ userId, groupId }: WebServices.UserAdmin.Group.GetSpacesByGroupIdAndUserId.Request) =>
			this.get(this.APIPath(groupId, "/users", userId, "spaces")),
		cache: AdminGroupUserServiceStatic.Cache.UserJoinedSpaceList
	});

	public resetPassword = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ userId, groupId }: WebServices.UserAdmin.GroupUser.ResetPassword.Request) =>
			this.post(`/api/admin/groups/${groupId}/users/reset_password/${userId}`),
		cache: AdminGroupUserServiceStatic.Cache.ResetPassword
	});

	public unlock = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ groupId, ...rest }: WebServices.UserAdmin.GroupUser.Unlock.Request) =>
			this.post(`/api/admin/groups/${groupId}/users/unlock`, rest),
		cache: AdminGroupUserServiceStatic.Cache.Unlock
	});

	public sendEmail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ groupId, ...rest }: WebServices.UserAdmin.GroupUser.SendEmail.Request) =>
			this.post(`/api/admin/groups/${groupId}/authentication_email`, rest),
		cache: AdminGroupUserServiceStatic.Cache.SendEmail
	});

	public createGroupUser = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ groupId, ...rest }: WebServices.UserAdmin.GroupUser.Add.Request) =>
			this.post(`/api/admin/groups/${groupId}/users`, rest),
		cache: AdminGroupUserServiceStatic.Cache.CreateGroupUser
	});

	public updateGroupUser = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ groupId, ...rest }: WebServices.UserAdmin.GroupUser.Update.Request) =>
			this.put(`/api/admin/groups/${groupId}/users`, rest),
		cache: AdminGroupUserServiceStatic.Cache.UpdateGroupUser
	});

	public userDetail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ groupId, userId }: WebServices.UserAdmin.GroupUser.UserDetail.Request) =>
			this.get(`/api/admin/groups/${groupId}/users/${userId}`),
		cache: AdminGroupUserServiceStatic.Cache.UserDetail
	});

	public downloadExcelSample = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ groupId }: WebServices.UserAdmin.GroupUser.DownloadExcelSample.Request) =>
			this.get(`/api/admin/groups/${groupId}/users/excel_import`, {}, { responseType: "blob" }),
		cache: AdminGroupUserServiceStatic.Cache.DownloadExcelSample
	});

	public importUsers = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({
			groupId,
			parameter
		}: WebServices.UserAdmin.GroupUser.ImportUsers.Request & { parameter: { file: File } }) =>
			this.post(`/api/admin/groups/${groupId}/users/excel_import`, parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			}),
		cache: AdminGroupUserServiceStatic.Cache.ImportUsers
	});
}

class UserAdminServiceStatic {
	constructor(
		public readonly group = new AdminGroupServiceStatic(),
		public readonly groupUsers = new AdminGroupUserServiceStatic()
	) {}
}

const UserAdminService = new UserAdminServiceStatic();
export default UserAdminService;
